import { SortCriteria } from 'src/app/services/models/dynamic-table.models';

export enum CellType {
  TEXT = 'text',
  FILTER_LIST = 'filter_list',
  MULTI_SELECT = 'multi_select',
  LIST_DISPLAY = 'list_display',
  DATA_DISPLAY = 'data_display',
  INPUT = 'input',
  CHECKBOX = 'checkbox',
  CHART = 'chart',
  ACTION = 'action',
  NOTE_BLOCK = 'note_block',
  RADIAL_BUTTON = 'radial_button',
  DATETIME_PICKER = 'datetime_picker',
  IMAGE = 'image',
  DATA_ENTRY = 'data_entry',
  LOOKUP = 'lookup',
  CALCULATION = 'calculation',
  LIST_CALC = 'list_calc',
  DATA_ENTRY_TABLE = 'data_entry_table',
  FILE_REFERENCE = 'file_reference',
  EMAIL_COMPOSE = 'email_compose',
  // PDF Cell Types
  PDF_TEXT = 'pdf_text',
  PDF_IMAGE = 'pdf_image',
  PDF_FIELD = 'pdf_field',
  PDF_TABLE = 'pdf_table',
  PDF_PAGE_BREAK = 'pdf_page_break',
  PDF_DATETIME = 'pdf_datetime',
  PDF_ROW = 'pdf_row',
  PDF_LIST_CALC = 'pdf_list_calc',
  PDF_REPEATING_SECTION = 'pdf_repeating_section',
  PDF_GRID = 'pdf_grid',
  PDF_CHECKBOX = 'pdf_checkbox',
  PDF_LINE = 'pdf_line',
  PDF_KEEP_TOGETHER_BLOCK = 'pdf_keep_together_block'
}

export interface SortCriteriaConfig {
  fieldId: number;
  fieldName: string; // For display purposes in the config UI
  direction: 'asc' | 'desc';
}

export interface CellPosition {
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex?: number;
}

export interface DataBinding {
  datasetId?: number;
  fieldName?: string;
  contextVariable?: string; // Reference to view manager context variable
  useContextFilter?: boolean; // Whether to filter data based on context
  dataObjectKey?: string; // Key to access data object in view manager context
  cellFilters?: CellFilter[]; // Cell-specific filters for select-entries calls
}

export type CellFilterMatchMode = 'exact' | 'not_equal' | 'starts_with' | 'greater_than' | 'less_than' | 'greater_than_or_equal' | 'less_than_or_equal' | 'between';

export interface CellFilter {
  fieldId: number;
  filterType: 'static' | 'context' | 'firstSelectedRow';
  matchMode?: CellFilterMatchMode; // Comparison operator — defaults to 'exact'
  staticValues?: any[]; // Static values when filterType is 'static'
  contextObject?: string; // Name of context object when filterType is 'context'
  contextField?: string; // Field from context object when filterType is 'context'
  sourceField?: string; // Field name from first selected row when filterType is 'firstSelectedRow'
  enabled?: boolean; // Whether this filter is active
}

export interface ContextObjectConfig {
  contextVariable: string; // Name/key for this context object
  datasetId: number; // Dataset to fetch data from
  filterFieldId?: number; // Field ID in the dataset to filter on (optional - if not specified, loads all fields)
  sourceContextVariable: string; // Source context object to get filter value from (for backward compatibility)
  sourceField: string; // Field from source context to use as filter value (for backward compatibility)
  loadFirstMatch?: boolean; // If true, load first matching record; if false, load all matches
  cellFilters?: CellFilter[]; // Cell-specific filters for this context object
  /**
   * Order the query returns rows in. Absent sends no ORDER BY, exactly as before, so every
   * context configured before this keeps the order it always had.
   *
   * It matters as much for a single-record context as for a list: with loadFirstMatch the
   * query is limited to one row, and which row that is has until now been whichever one the
   * database happened to return first.
   */
  sortBy?: SortCriteriaConfig[];
  loadMode?: 'init' | 'onChange'; // When to load: on initialization (default) or when cell value changes
  loadOnCreateNew?: boolean; // Also load this context when the Create New button fires (row-independent auxiliary data only)
}

export interface CellConfiguration {
  // Text Cell Config (also used by Data Display for value-text styling)
  text?: string;
  textMode?: 'static' | 'dynamic'; // Whether to use static text or dynamic data
  fontSize?: number;
  fontWeight?: string;
  textAlign?: string;
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline';

  // Filter List Config
  datasetId?: string;
  displayColumns?: string[]; // Columns to display in the table
  selectionMode?: 'single' | 'multiple';
  enableRowSelection?: boolean; // Whether to show checkboxes for row selection
  enableNavigation?: boolean;
  storeNavigationContext?: boolean; // Whether to store context data when navigating to tabs
  contextVariable?: string; // Key for storing selected row data in view manager (deprecated - use primaryContext)

  // Row Upsert — writes the clicked row into an existing context object.
  // Single context → replace. List context (records array) → append (dedup by deep-equal).
  enableRowUpsert?: boolean;
  rowUpsertContextVariable?: string;
  // When set, the clicked row is projected through these mappings instead of
  // being written verbatim. Lets admins translate the filter list's row shape
  // (e.g. inventory dataset) into the target context's shape (e.g. invList2
  // with different field names).
  rowUpsertFieldMappings?: UpsertFieldMapping[];

  // Primary context - stores the row data from the main dataset
  enablePrimaryContext?: boolean; // Whether to store the selected row data
  primaryContextVariable?: string; // Name for the primary context object

  // Additional contexts - fetch related data from other datasets
  additionalContexts?: ContextObjectConfig[]; // Additional context objects that lookup related data

  // Filter List - Create New Entry Config
  allowCreate?: boolean; // Enable "Create New" button
  createMode?: 'inline' | 'navigate'; // How to create new entries
  createNavigateTabId?: string; // Tab to navigate to (for navigate mode)
  createButtonLabel?: string; // Optional custom label for create button
  createEmptyContexts?: CreateEmptyContextConfig[]; // Empty contexts to create (for navigate mode)
  storeAllRowsInContext?: boolean; // Store all loaded rows into a context variable (default: false)
  allRowsContextVariable?: string; // Context variable name for storing all loaded rows
  allowDelete?: boolean; // Enable row deletion with trash icon (default: false)
  createExcludedFields?: string[]; // Field names to hide from inline create form (will send null)
  createPrePopulatedFields?: CreatePrePopulatedField[]; // Fields to pre-populate from context

  // Data Display/Input Config
  fieldName?: string;
  dataType?: string;
  format?: string;
  displayFormat?: string;
  displayLayout?: 'inline' | 'stacked'; // Layout for displaying label and value
  editable?: boolean;
  label?: string;
  placeholder?: string; // Placeholder text for input fields
  defaultValue?: any; // Default value for input fields
  dataSource?: 'context' | 'dataset' | 'none'; // Source of data for input field
  inputType?: 'free' | 'static' | 'dataset'; // Type of input: free text, static list, or dataset options
  options?: any[]; // Manual options for static dropdown
  optionsDatasetId?: number; // Dataset ID for dataset-based dropdown options
  optionsDisplayField?: string; // Field to display in the dropdown
  optionsValueField?: string; // Field to save as the value
  optionsFetchMode?: 'all' | 'minimal'; // Whether to fetch all fields or just display/value fields (default: all)

  // Lookup Input Config (for Input Cell lookup type)
  lookupDatasetId?: number; // Dataset to pull lookup data from
  lookupDisplayColumns?: string[]; // Columns to show in modal table
  lookupFilterColumns?: string[]; // Columns user can filter on in modal
  lookupValueField?: string; // Field to store as the input value
  lookupDisplayField?: string; // Field to show in input after selection
  lookupCellFilters?: CellFilter[]; // Pre-filters for lookup data (can use context values)
  lookupAllowCreate?: boolean; // Allow creating new entries in lookup modal
  lookupCreateContext?: boolean; // Store entire selected row in context object
  lookupContextVariable?: string; // Name of context variable for full row

  // Lookup Cell Config (standalone lookup button cell)
  lookupButtonLabel?: string; // Optional label for the button
  lookupContextMappings?: LookupContextMapping[]; // Map selected row fields to context objects
  lookupStoreSelectedRow?: boolean; // Store the entire selected row in a context variable (mirrors Filter List primary context)
  lookupSelectedRowContextVariable?: string; // Name of the context variable to store the selected row in
  lookupPresetFilterFields?: string[]; // Filter fields that always appear pre-loaded in the lookup modal
  lookupFilterDisplayMode?: 'dialog' | 'inline'; // How lookup modal filters are displayed (default: dialog)
  lookupInlineFilterMatchMode?: string; // Default match mode for lookup inline filters
  dataLoadLimit?: number; // Max records to load (1-2000, default 100)
  presetFilterFields?: string[]; // Filter fields that always appear pre-loaded in the filter dialog
  filterColumns?: string[]; // Which fields appear in the filter dropdown (and in what order). If empty, all columns are shown.
  filterDisplayMode?: 'dialog' | 'inline'; // How user filters are displayed (default: dialog)
  inlineFilterMatchMode?: string; // Default match mode for inline filters (e.g. 'partial', 'exact')
  distinct?: boolean; // Return only distinct rows from the dataset (default: false)
  orderBy?: SortCriteriaConfig[]; // Server-side sorting by one or more fields

  // Appearance
  alternateRowColors?: boolean; // Enable alternating row colors
  rowEvenColor?: string; // Background color for even rows
  rowOddColor?: string; // Background color for odd rows
  firstColumnBold?: boolean; // Make the first column header and values bold

  // Transient runtime state (not persisted to backend)
  _activeFilterState?: any[]; // Saved filter rows for restoring after tab switch
  _activeFilterHasActive?: boolean; // Whether filters were active
  _lastFilterSignature?: string; // Data table: filter signature when data was last loaded

  // Multi-Select Cell Config
  multiSelectFieldsToStore?: string[]; // Which fields to store from each selected row (empty = all fields)
  multiSelectContextVariable?: string; // Context variable name for storing selected items array
  autoSelectFirst?: boolean; // Automatically select the first row when data loads (default: false)
  navigationButtonLabel?: string; // Label for navigation button (used by multi-select and filter-list)

  // List Display Cell Config
  listDisplayLabel?: string; // Label text at the top of the list
  listDisplayLabelFontSize?: number;
  listDisplayLabelFontWeight?: 'normal' | 'bold';
  listDisplayLabelFontStyle?: 'normal' | 'italic';
  listDisplayLabelColor?: string;
  listDisplayContextVariable?: string; // Context variable containing the array to display
  listDisplayRowElements?: ListDisplayRowElement[]; // Template for each list item row
  listDisplayRowFontSize?: number;
  listDisplayRowFontWeight?: 'normal' | 'bold';
  listDisplayRowFontStyle?: 'normal' | 'italic';
  listDisplayRowColor?: string;
  listDisplayRowSpacing?: number; // Vertical spacing between rows in pixels
  listDisplayEmptyMessage?: string; // Message to show when list is empty

  // Compose Email Config
  emailComposeContextVariable?: string; // Array context holding the selected rows
  emailComposeEmailField?: string; // Field on each row containing the email address
  emailComposeRecipientMode?: 'to' | 'cc' | 'bcc'; // Which header the addresses go in
  emailComposeButtonText?: string;
  emailComposeButtonColor?: 'primary' | 'accent' | 'warn';
  emailComposeSubject?: string; // Optional pre-filled subject
  emailComposeBody?: string; // Optional pre-filled body
  emailComposeShowCount?: boolean; // Show the recipient count on the button

  // Checkbox Config
  checkboxLabel?: string; // Label text for the checkbox
  checkedValue?: any; // Value to store when checked (default: true)
  uncheckedValue?: any; // Value to store when unchecked (default: false)

  // Note Block - Optional Tabs
  noteBlockEnableTabs?: boolean; // When true, the note cell renders a tab strip and each tab maps to a different field on the bound context object
  noteBlockTabs?: NoteBlockTab[];

  // Input Field - additional context mappings applied on every value change
  // (alongside the cell's primary binding). Mirrors the LookupContextMapping shape.
  inputContextMappings?: InputContextMapping[];

  // Action Config
  actionType?: 'navigate' | 'save' | 'clear_context';
  navigationTabId?: string; // Navigate to specific tab
  buttonText?: string;
  buttonStyle?: 'raised' | 'flat' | 'stroked';
  buttonColor?: 'primary' | 'accent' | 'warn';
  saveDataSource?: 'context' | 'dataset'; // Where to save data
  saveMode?: 'update' | 'add' | 'auto' | 'upsert'; // How to save: update existing, add new, auto-detect, or upsert (insert-or-update by PK)
  saveContextFieldMappings?: SaveContextFieldMapping[]; // Copy fields from other contexts into the save target before saving
  saveOperations?: SaveOperation[]; // Ordered list of save operations for chained saves
  autoTrimStrings?: boolean; // Strip leading/trailing whitespace from string fields before saving (default: true)
  postSaveRefreshCellIds?: string[]; // Cell IDs to refresh after a successful save
  clearContextVariables?: string[]; // Context variables to clear (for clear_context action)

  // Data Entry Table Config
  allowDeleteRows?: boolean;
  loadInitialData?: boolean;
  addRowButtons?: AddRowButton[]; // Configurable add-row buttons with preset values
  columnSettings?: { [fieldName: string]: ColumnFieldSetting }; // Per-column input type config

  // File Reference Config
  fileRefLabel?: string;

  // Chart Config
  chartType?: 'line' | 'bar' | 'pie' | 'doughnut';
  xAxis?: string;
  yAxis?: string;

  // Radial Button Config
  layoutDirection?: 'auto' | 'horizontal' | 'vertical'; // Layout direction for radial buttons
  columnsCount?: number; // Number of columns for grid layout (0 = auto)

  // Note Block Config
  noteTitle?: string;
  noteContent?: string;

  // DateTime Picker Config
  dateTimeMode?: 'date' | 'time' | 'datetime' | 'day_range' | 'month_range' | 'year_range';
  dateFormat?: string; // Date display format
  timeFormat?: string; // Time display format (12h/24h)
  enableClear?: boolean; // Allow clearing the value
  minDate?: Date; // Minimum selectable date
  maxDate?: Date; // Maximum selectable date
  rangeStorageMode?: 'existing' | 'new'; // Store range in existing context or new variables
  rangeEndContextObjectKey?: string; // Context object for range end value
  rangeEndFieldName?: string; // Field for range end value
  rangeStartContextVariable?: string; // New context variable for range start
  rangeEndContextVariable?: string; // New context variable for range end

  // Image Config
  imageSource?: 'static' | 'context'; // Source of image: static file path or context data
  staticFilePath?: string; // File path when using static source
  imageWidth?: number; // Width in pixels (0 = auto/fit to cell)
  imageHeight?: number; // Height in pixels (0 = auto/fit to cell)
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'; // How image fits in cell
  altText?: string; // Alternative text for accessibility

  // Data Entry Config
  targetDatasetId?: number; // Dataset to add entries to
  fieldMappings?: DataEntryFieldMapping[]; // Mapping of dataset fields to input sources
  submitButtonText?: string; // Text for the submit button
  clearAfterSubmit?: boolean; // Whether to clear form after successful submission

  // PDF Text Config
  pdfFontSize?: number;
  pdfFontWeight?: 'normal' | 'bold';
  pdfFontStyle?: 'normal' | 'italic';
  pdfTextAlign?: 'left' | 'center' | 'right';
  pdfColor?: string;
  pdfValue?: string; // Static text value for PDF

  // PDF Image Config
  pdfImageSource?: 'static' | 'context';
  pdfFilePath?: string; // File path from FileController upload
  pdfImageWidth?: number;
  pdfImageHeight?: number;

  // PDF Field Config
  pdfFieldLabel?: string;
  pdfShowLabel?: boolean;
  pdfFieldFormat?: 'raw' | 'currency' | 'number' | 'percent' | 'date'; // Value formatting (defaults to raw)
  pdfOverrideValue?: string; // Manual override from PDF edit mode (ephemeral, not saved)
  _isPdfEditAddition?: boolean; // Marks cells added during PDF edit mode (ephemeral)
  _pdfAbsoluteY?: boolean; // Cell dragged over a repeating section in PDF edit mode; position.y is in absolute (section-expanded) coords (ephemeral)

  // PDF Table Config
  pdfTableColumns?: PdfTableColumn[];
  pdfShowHeader?: boolean;
  pdfHeaderFontSize?: number;
  pdfHeaderFontWeight?: 'normal' | 'bold';
  pdfRowFontSize?: number;

  // PDF DateTime Config
  pdfDateTimeValue?: PdfDateTimeValue;
  pdfDateTimeLabel?: string;
  pdfDateTimeShowLabel?: boolean;
  pdfDateTimeFontSize?: number;
  pdfDateTimeFormat?: string; // Date format string (e.g., 'MM/dd/yyyy', 'MMMM d, yyyy')

  // Calculation Cell Config (standard + PDF)
  calcUseConditional?: boolean; // Enable conditional calculations based on a context value
  calcConditionContextVariable?: string; // Context object to check for condition value
  calcConditionFieldName?: string; // Field in the context object that holds the condition value
  calcConditions?: CalcCondition[]; // Conditional operand sets — each matches a field value to its own formula
  calcOperands?: CalcOperand[];
  calcConstraints?: CalcConstraint[];
  calcResultFormat?: 'number' | 'currency' | 'percent';
  calcLabel?: string;
  calcDecimalPlaces?: number;
  calcStoreInContext?: boolean;
  calcStoreMode?: 'newVariable' | 'existingField'; // How to store the result
  calcContextVariable?: string; // For newVariable mode: name of new context variable
  calcNewVariableFieldName?: string; // For newVariable mode: field name within the new context object
  calcStoreContextObject?: string; // For existingField mode: which context object to update
  calcStoreFieldName?: string; // For existingField mode: which field in that context object
  calcFontSize?: number;
  calcFontWeight?: 'normal' | 'bold';

  // List Calculation Cell Config (standard — aggregates over a context array)
  listCalcContextVariable?: string; // Context variable containing the array to aggregate
  listCalcFieldName?: string; // Field from each item to aggregate
  listCalcInlineOperands?: InlineCalcOperand[]; // Per-row operations before aggregation
  listCalcOperation?: 'sum' | 'average' | 'count' | 'min' | 'max';
  listCalcPostOperands?: CalcOperand[]; // Operations after aggregation
  listCalcFormat?: 'number' | 'currency' | 'percent';
  listCalcLabel?: string;
  listCalcShowLabel?: boolean;
  listCalcDecimalPlaces?: number;
  listCalcFontSize?: number;
  listCalcFontWeight?: 'normal' | 'bold';
  listCalcStoreInContext?: boolean;
  listCalcStoreMode?: 'newVariable' | 'existingField';
  listCalcStoreContextVariable?: string;
  listCalcNewVariableFieldName?: string;
  listCalcStoreContextObject?: string;
  listCalcStoreFieldName?: string;

  // PDF List Calculation Config
  pdfListCalcContextVariable?: string;
  pdfListCalcFieldName?: string;
  pdfListCalcFilters?: ListCalcFilter[];  // Filter rows before aggregation
  pdfListCalcInlineOperands?: InlineCalcOperand[];  // Per-row operations before aggregation
  pdfListCalcOperation?: 'sum' | 'average' | 'count' | 'min' | 'max';
  pdfListCalcPostOperands?: CalcOperand[];  // Operations to apply after aggregation
  pdfListCalcFormat?: 'number' | 'currency' | 'percent';
  pdfListCalcLabel?: string;
  pdfListCalcShowLabel?: boolean;
  pdfListCalcDecimalPlaces?: number;
  pdfListCalcFontSize?: number;
  pdfListCalcFontWeight?: 'normal' | 'bold';
  pdfListCalcStoreInContext?: boolean;
  pdfListCalcStoreMode?: 'newVariable' | 'existingField';
  pdfListCalcStoreContextVariable?: string; // For newVariable mode
  pdfListCalcNewVariableFieldName?: string; // For newVariable mode: field name within the new context object
  pdfListCalcStoreContextObject?: string; // For existingField mode: which context object to update
  pdfListCalcStoreFieldName?: string; // For existingField mode: which field to write

  // PDF Checkbox Config
  pdfCheckboxChecked?: boolean;
  pdfCheckboxLabel?: string;
  pdfCheckboxBoxSize?: number;
  pdfCheckboxBoxColor?: string;
  pdfCheckboxCheckColor?: string;
  pdfCheckboxBorderThickness?: number;
  pdfCheckboxGap?: number;
  // Checkbox data binding (optional - resolve checked from context)
  pdfCheckboxContextVariable?: string;
  pdfCheckboxFieldName?: string;

  // PDF Grid Config
  pdfGridColumns?: PdfGridDimension[];
  pdfGridRows?: PdfGridDimension[];
  pdfGridShowBorders?: boolean;
  pdfGridBorderColor?: string;
  pdfGridBorderThickness?: number;
  pdfGridBackgroundColor?: string;

  // PDF Separator Line Config
  pdfLineOrientation?: 'horizontal' | 'vertical';
  pdfLineStyle?: 'solid' | 'dashed' | 'dotted';
  pdfLineColor?: string;
  pdfLineThickness?: number;

  // PDF Row Container Config
  pdfRowContainerElements?: PdfRowElement[];
  pdfRowContainerFontSize?: number;
  pdfRowContainerFontWeight?: 'normal' | 'bold';
  pdfRowContainerFontStyle?: 'normal' | 'italic';
  pdfRowContainerColor?: string;
  pdfRowContainerGap?: number; // Gap between elements in pixels (0 for no gap)
  pdfRowContainerAlign?: 'left' | 'center' | 'right';
}

export interface PdfDateTimeValue {
  mode: 'relative' | 'specific';
  value: any; // For relative: string (e.g., 'today', 'custom'), for specific: Date or ISO string
  customDays?: number;
  customMonths?: number;
  customYears?: number;
}

// PDF Row Container - inline elements that flow horizontally
export interface PdfRowElement {
  id: string;
  type: 'text' | 'field' | 'datetime';
  // For text type
  textValue?: string;
  // For field type
  contextVariable?: string;
  fieldName?: string;
  fieldFormat?: string; // 'currency', 'date', 'number', etc.
  // For datetime type
  dateTimeValue?: PdfDateTimeValue;
  dateTimeFormat?: string;
}

// List Display Row Element - used to define how each item in a list is rendered
export interface ListDisplayRowElement {
  id: string;
  type: 'text' | 'field';
  // For text type - static text like "Make: " or ", Model: "
  textValue?: string;
  // For field type - dynamic field from each list item
  fieldName?: string; // Field name from the list item object
  fieldFormat?: string; // 'currency', 'date', 'number', 'percent', etc.
}

export interface DataEntryFieldMapping {
  fieldName: string; // Field name in the target dataset
  inputSource: 'free' | 'static' | 'context' | 'null'; // Type of input source
  staticValue?: any; // Value when inputSource is 'static'
  contextObjectKey?: string; // Context object when inputSource is 'context'
  contextFieldName?: string; // Field in context object when inputSource is 'context'
  label?: string; // Display label for the field (for free input)
  placeholder?: string; // Placeholder text (for free input)
}

// Lookup Cell - maps selected row fields to context object fields
export interface LookupContextMapping {
  id: string; // Unique identifier for this mapping
  contextVariable: string; // Name of the context object to update
  fieldMappings: LookupFieldMapping[]; // Individual field mappings
  relatedDataFetch?: LookupRelatedDataFetch; // Optionally fetch related data from another dataset
}

export interface LookupRelatedDataFetch {
  datasetId: number; // Dataset to fetch related data from
  filterFieldId: number; // Field ID in the related dataset to filter on
  sourceField: string; // Field from the primary lookup result to use as filter value
  selectionMode: 'first' | 'lowest' | 'highest' | 'match'; // How to pick the row from results
  selectionFieldId?: number; // Field ID used for server-side sorting (lowest/highest)
  selectionFieldName?: string; // Field name used for client-side match
  matchValue?: string; // Value to match when selectionMode is 'match'
  limit?: number; // How many rows to fetch (default: 1)
}

export interface LookupFieldMapping {
  sourceField: string; // Field name from the selected lookup row
  targetField: string; // Field name in the context object to update
  sourceFields?: string[]; // Multiple source fields to combine into targetField
  separator?: string; // Separator between combined fields (default: ' ')
}

// Filter List Cell - empty context configuration for "Create New" navigate mode
export interface CreateEmptyContextConfig {
  contextVariable: string; // Name of the context variable to create (e.g., "NewInventory")
  datasetId: number; // Dataset to get schema from for building empty object
  contextType?: 'single' | 'list'; // 'single' (default) — creates a flat empty record;
                                    // 'list' — creates an empty array []
}

// Filter List Cell - pre-populated field for inline "Create New"
export interface CreatePrePopulatedField {
  fieldName: string; // Field name in the dataset to pre-populate
  contextObject: string; // Context object to get the value from
  contextField: string; // Field in the context object to use as the value
}

// Note Block - one tab entry. label is shown in the tab strip, fieldName is the
// key on the bound context object whose value backs that tab's textarea.
export interface NoteBlockTab {
  id: string;
  label: string;
  fieldName: string;
}

// Filter List Row Upsert - projects the clicked row's fields into the target
// context's shape. Mirrors LookupFieldMapping.
export interface UpsertFieldMapping {
  sourceField: string;     // Field on the clicked row (from filter list's dataset)
  targetField: string;     // Field on the target context object to write into
  sourceFields?: string[]; // Multi-source combine (overrides sourceField)
  separator?: string;      // Combine separator (default ' ')
}

// Input Field - one mapping entry per TARGET context object. The runtime writes
// each fieldMapping into the named contextVariable whenever the input value changes.
// Mirrors LookupContextMapping but adds a 'source' discriminator on each field
// mapping since input cells can pull from either the cell's current value or a
// dataset row (when inputType=dataset).
export interface InputContextMapping {
  id: string;
  contextVariable: string;
  fieldMappings: InputFieldMapping[];
}

export interface InputFieldMapping {
  targetField: string;                        // Field on the context object to write into
  source: 'value' | 'datasetField';           // Where the value comes from
  sourceField?: string;                       // Single field from the selected dataset row (datasetField source)
  sourceFields?: string[];                    // Multi-source combine (datasetField source)
  separator?: string;                         // Combine separator (default ' ')
}

// Action Cell - Field mapping from another context object (for single saves).
// sourceContextVariable may also be a reserved system source: '$user' or '$now'.
export interface SaveContextFieldMapping {
  sourceContextVariable: string; // Context object to pull the value from (or '$user' / '$now')
  sourceField: string; // Field name in the source context
  targetField: string; // Field name in the save target to write the value into
  onlyIfNew?: boolean; // If true, only apply to rows tagged as new (skip on edits) — for createdBy/createdDate
}

// Action Cell - Save Operation for chained saves
export interface SaveOperationFieldMapping {
  sourceOperationId: string; // ID of the source operation to get value from
  sourceField: string; // Field from that operation's response
  targetField: string; // Field in this context object to update before saving
}

export interface SaveOperation {
  id: string; // Unique identifier for this operation
  order: number; // Execution order (1, 2, 3...)
  contextVariable: string; // Which context object to save
  datasetId: number; // Which dataset to save to
  saveMode?: 'update' | 'add' | 'auto' | 'delete' | 'upsert'; // Optional override (defaults to cell's saveMode)
  fieldMappingsFromPrevious?: SaveOperationFieldMapping[]; // Copy values from previous results
  contextFieldMappings?: SaveContextFieldMapping[]; // Copy values from other context objects before saving
}

// Data Entry Table - add row button configuration
export interface AddRowButton {
  label: string;
  presetValues: { fieldName: string; value: string }[];
}

// Data Entry Table - per-column input type settings
export interface ColumnFieldSetting {
  inputType: 'free' | 'dataset' | 'static' | 'calculated';
  optionsDatasetId?: number;
  optionsDisplayField?: string;
  optionsValueField?: string;
  staticOptions?: { value: string; label: string }[];
  // Calculated column settings
  calcOperands?: ColumnCalcOperand[];
  calcUseConditional?: boolean;
  // New conditional shape: multi-clause AND rules with a final-catchall default formula.
  calcConditionalRules?: CalcConditionalRule[];
  calcDefaultOperands?: ColumnCalcOperand[]; // Used when no rule matches
  // Legacy single-field-equality conditional (kept for backward compatibility — read at runtime,
  // migrated to calcConditionalRules when the column is re-edited in the builder).
  calcConditionRowField?: string;
  calcConditions?: { conditionValue: string; operands: ColumnCalcOperand[] }[];
  calcResultFormat?: 'number' | 'currency' | 'percent';
  calcDecimalPlaces?: number;
  calcConstraints?: CalcConstraint[];
}

// Data Entry Table - one clause within a conditional rule. Clauses in a rule are ANDed.
export interface CalcConditionClause {
  id: string;
  leftSource: 'row_field' | 'context_field';
  leftRowField?: string;        // when leftSource = 'row_field'
  leftContextVariable?: string; // when leftSource = 'context_field'
  leftFieldName?: string;       // when leftSource = 'context_field'
  comparator: 'equals' | 'not_equals';
  compareValue?: string;        // free-text right-hand side
  compareNullOrEmpty?: boolean; // when true, ignore compareValue and match null/undefined/empty/whitespace
}

// Data Entry Table - one conditional rule: when all clauses pass, use these operands.
export interface CalcConditionalRule {
  id: string;
  clauses: CalcConditionClause[];
  operands: ColumnCalcOperand[];
}

// Data Entry Table - operand that can reference row fields, context fields, or context lookups
export interface ColumnCalcOperand {
  id: string;
  sourceType: 'row_field' | 'context_field' | 'context_lookup' | 'value';
  operator: '+' | '-' | '*' | '/';
  // row_field: read from current row
  rowFieldName?: string;
  // context_field: read from a context object
  contextVariable?: string;
  fieldName?: string;
  // context_lookup: find a row in a context array where a field matches a row field value
  lookupContextVariable?: string;  // Context array to search
  lookupMatchField?: string;       // Field in context array to match against
  lookupMatchRowField?: string;    // Row field whose value must match
  lookupReturnField?: string;      // Field from matched context row to return
  // value: literal number
  literalValue?: number;
}

// Calculation Cell - operand configuration
// Inline operand - references fields from the same row item (no context needed)
export interface InlineCalcOperand {
  id: string;
  sourceType: 'field' | 'value';
  fieldName: string;           // Field from the same row item (when sourceType = 'field')
  literalValue?: number;       // Fixed numeric value (when sourceType = 'value')
  operator: '+' | '-' | '*' | '/';
}

// Calculation Cell - conditional calculation (different formula per value)
export interface CalcCondition {
  id: string;
  conditionValue: string; // The value to match (e.g., "Buy", "Sell", "Manager")
  operands: CalcOperand[]; // The formula operands to use when this condition matches
}

// Calculation Cell - post-calculation constraint applied to the result
export interface CalcConstraint {
  id: string;
  type: 'min' | 'max' | 'round' | 'abs';
  value?: number; // For min/max: the limit value. For round: not used. For abs: not used.
  roundMode?: 'nearest' | 'up' | 'down'; // For round type only
}

// Calculation Cell - operand that references context data
export interface CalcOperand {
  id: string;
  sourceType: 'field' | 'value';         // Context field or literal value
  contextVariable: string;   // Which context object (when sourceType = 'field')
  fieldName: string;          // Which field from that context (when sourceType = 'field')
  literalValue?: number;      // Fixed numeric value (when sourceType = 'value')
  operator: '+' | '-' | '*' | '/';       // Operation to apply
}

// List Calc filter - filter rows before aggregation
export interface ListCalcFilter {
  id: string;
  fieldName: string;                     // Field to check on each row
  operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than';
  value: string;                         // Value to compare against
}

// PDF Grid dimension - either fixed size or proportional weight
export interface PdfGridDimension {
  width?: number;   // Fixed width in canvas units (for columns)
  height?: number;  // Fixed height in canvas units (for rows)
  weight?: number;  // Proportional weight for remaining space
}

export interface PdfTableColumn {
  fieldName: string;
  headerText: string;
  width: number; // Percentage or fixed width
  alignment: 'left' | 'center' | 'right';
  format?: string; // Date/number formatting
}

export interface PdfTabConfig {
  pageSize: 'A4' | 'Letter' | 'Legal';
  orientation: 'portrait' | 'landscape';
  margins: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
}

export interface ViewCell {
  id: string;
  type: CellType;
  position: CellPosition;
  configuration: CellConfiguration;
  dataBinding: DataBinding;
}

export interface ViewCanvas {
  width: number;
  height: number;
}

export interface ViewManager {
  contextData: { [key: string]: any };
  contextInstructions: { [key: string]: any };
  globalFilters?: { [key: string]: any };
}

// PDF Repeating Section - a full-width band on the canvas that contains regular cells
// which repeat for each data item from the data source
export interface ViewRepeatingSection {
  id: string;
  y: number; // Y position on the canvas
  height: number; // Height of one row template
  dataSource: 'dataset' | 'context';
  datasetId?: number;
  contextVariable?: string;
  cellFilters?: CellFilter[];
  /**
   * Order the dataset query returns rows in. Absent means the query is sent without an
   * ORDER BY, exactly as before - which is what keeps every section built before this
   * returning rows in the order it always did.
   */
  sortBy?: SortCriteria[];
  cells: ViewCell[]; // Regular PDF cells positioned within this section
  rowBorder?: boolean;
  alternateRowColor?: string;
  storeContextVariable?: string; // Store query results to this context variable as an array
}

// PDF Keep-Together Block - a full-width band (just a y + height) used purely as an
// authoring tool. Cells whose bounds fall inside the band are registered as members
// (via the band's "register" button). At PDF generation the member ids are sent to
// the backend, which keeps them on the same page (shifting the whole block down if
// it would straddle a page boundary). The band itself is not rendered in the PDF.
export interface ViewKeepTogetherBlock {
  id: string;
  y: number;
  height: number;
  memberCellIds: string[];
}

export interface ViewTab {
  id: string;
  name: string;
  order: number;
  tabType?: 'standard' | 'pdf'; // Type of tab - defaults to 'standard' for backward compatibility
  canvas: ViewCanvas;
  cells: ViewCell[];
  pdfConfig?: PdfTabConfig; // PDF-specific configuration (only for PDF tabs)
  requiredContexts?: string[]; // Context objects that must be loaded before this tab is enabled
  repeatingSections?: ViewRepeatingSection[]; // PDF repeating sections
  keepTogetherBlocks?: ViewKeepTogetherBlock[]; // PDF keep-together blocks (authoring metadata)
}

export interface ViewDefinition {
  id: string;
  name: string;
  description: string;
  viewManager: ViewManager;
  tabs: ViewTab[];
  activeTabId?: string;
  isActive: boolean;
  isShared: boolean;
  displayOrder?: number;
  createdBy: string;
  lastModified: Date;
}

export interface CellTypeOption {
  type: CellType;
  label: string;
  icon: string;
  description: string;
}

export const CELL_TYPE_OPTIONS: CellTypeOption[] = [
  {
    type: CellType.TEXT,
    label: 'Text',
    icon: 'text_fields',
    description: 'Static text or headers'
  },
  {
    type: CellType.FILTER_LIST,
    label: 'Filter List',
    icon: 'filter_list',
    description: 'Filterable list from dataset'
  },
  {
    type: CellType.MULTI_SELECT,
    label: 'Multi-Select List',
    icon: 'checklist',
    description: 'Select multiple rows from dataset'
  },
  {
    type: CellType.LIST_DISPLAY,
    label: 'List Display',
    icon: 'format_list_bulleted',
    description: 'Display list items from context array'
  },
  {
    type: CellType.DATA_DISPLAY,
    label: 'Data Display',
    icon: 'visibility',
    description: 'Display data field'
  },
  {
    type: CellType.INPUT,
    label: 'Input Field',
    icon: 'input',
    description: 'Editable input field'
  },
  {
    type: CellType.RADIAL_BUTTON,
    label: 'Radial Button',
    icon: 'radio_button_checked',
    description: 'Radio button selection'
  },
  {
    type: CellType.DATETIME_PICKER,
    label: 'Date Time Picker',
    icon: 'schedule',
    description: 'Date and time selection'
  },
  {
    type: CellType.CHECKBOX,
    label: 'Checkbox',
    icon: 'check_box',
    description: 'Boolean value checkbox'
  },
  // {
  //   type: CellType.CHART,
  //   label: 'Chart',
  //   icon: 'bar_chart',
  //   description: 'Data visualization'
  // },
  {
    type: CellType.NOTE_BLOCK,
    label: 'Note Block',
    icon: 'notes',
    description: 'Multi-line text notes'
  },
  {
    type: CellType.IMAGE,
    label: 'Image',
    icon: 'image',
    description: 'Display image from file'
  },
  {
    type: CellType.FILE_REFERENCE,
    label: 'File Reference',
    icon: 'attach_file',
    description: 'Browse and select a file, stores path to context'
  },
  {
    type: CellType.DATA_ENTRY,
    label: 'Data Entry',
    icon: 'post_add',
    description: 'Form to add new dataset entries'
  },
  {
    type: CellType.LOOKUP,
    label: 'Lookup Button',
    icon: 'more_horiz',
    description: 'Lookup button to search and select data'
  },
  {
    type: CellType.ACTION,
    label: 'Action Button',
    icon: 'smart_button',
    description: 'Action buttons'
  },
  {
    type: CellType.CALCULATION,
    label: 'Calculation',
    icon: 'calculate',
    description: 'Calculate values from context fields'
  },
  {
    type: CellType.LIST_CALC,
    label: 'List Calculation',
    icon: 'functions',
    description: 'Aggregate values from a list (sum, avg, etc.)'
  },
  {
    type: CellType.DATA_ENTRY_TABLE,
    label: 'Data Entry Table',
    icon: 'edit_note',
    description: 'Editable table for adding and editing rows'
  },
  {
    type: CellType.EMAIL_COMPOSE,
    label: 'Compose Email',
    icon: 'email',
    description: 'Open an email to the selected recipients'
  },
];

export const PDF_CELL_TYPE_OPTIONS: CellTypeOption[] = [
  {
    type: CellType.PDF_TEXT,
    label: 'Text',
    icon: 'text_fields',
    description: 'Static text or headers'
  },
  {
    type: CellType.PDF_FIELD,
    label: 'Dynamic Field',
    icon: 'data_object',
    description: 'Dynamic value from context'
  },
  {
    type: CellType.PDF_TABLE,
    label: 'Table',
    icon: 'table_chart',
    description: 'Dynamic table from context'
  },
  {
    type: CellType.LIST_DISPLAY,
    label: 'List Display',
    icon: 'format_list_bulleted',
    description: 'Display list items from context array'
  },
  {
    type: CellType.PDF_IMAGE,
    label: 'Image',
    icon: 'image',
    description: 'Static or dynamic image'
  },
  {
    type: CellType.PDF_DATETIME,
    label: 'Date/Time',
    icon: 'event',
    description: 'Relative or specific date'
  },
  {
    type: CellType.PDF_ROW,
    label: 'Row Container',
    icon: 'view_stream',
    description: 'Horizontal row of inline elements'
  },
  {
    type: CellType.PDF_PAGE_BREAK,
    label: 'Page Break',
    icon: 'insert_page_break',
    description: 'Force a page break'
  },
  {
    type: CellType.CALCULATION,
    label: 'Calculation',
    icon: 'calculate',
    description: 'Calculate values from context fields'
  },
  {
    type: CellType.PDF_LIST_CALC,
    label: 'List Calculation',
    icon: 'functions',
    description: 'Aggregate values from a list'
  },
  {
    type: CellType.PDF_REPEATING_SECTION,
    label: 'Repeating Section',
    icon: 'view_list',
    description: 'Full-width section that repeats for each data item'
  },
  {
    type: CellType.PDF_GRID,
    label: 'Grid',
    icon: 'grid_on',
    description: 'Visual grid layout with configurable rows and columns'
  },
  {
    type: CellType.PDF_CHECKBOX,
    label: 'Checkbox',
    icon: 'check_box',
    description: 'Checkbox with label for PDF output'
  },
  {
    type: CellType.PDF_LINE,
    label: 'Separator Line',
    icon: 'horizontal_rule',
    description: 'A line to separate parts of the document'
  },
  {
    type: CellType.PDF_KEEP_TOGETHER_BLOCK,
    label: 'Keep-Together Block',
    icon: 'vertical_align_center',
    description: 'Full-width band; cells inside stay on the same page'
  },
];

export const DEFAULT_PDF_CONFIG: PdfTabConfig = {
  pageSize: 'A4',
  orientation: 'portrait',
  margins: {
    top: 40,
    right: 40,
    bottom: 40,
    left: 40
  }
};
